// pages/ProfilePage.js - Complete Working with Fixed Back Button
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrders } from '../../hooks/useOrders';
import { dbSet, dbGet, cleanFirebaseData, sanitizeFirebasePath } from '../../config/firebase';
import BillReceipt from '../../components/website/BillReceipt';
import {
  User, Package, Phone, Mail, MapPin, Edit3, Save,
  LogOut, Truck, CheckCircle, XCircle, History,
  ShoppingBag, Calendar, ArrowLeft, Check,
  Home, Building, Navigation, AlertCircle, Loader, Key, Clock, Copy, RefreshCw,
  ChevronRight, Star, Gift, Heart, Shield, Award, Flower2
} from 'lucide-react';
import { toast } from 'react-toastify';

const getSafeUserId = (user) => {
  if (!user) return 'anonymous_user';
  const userId = user.$id || user.uid || user.id;
  if (userId && typeof userId === 'string') return sanitizeFirebasePath(userId);
  if (user.email) return sanitizeFirebasePath(user.email);
  return 'anonymous_user';
};

const ProfilePage = ({ onClose, defaultTab = 'profile' }) => {
  const navigate = useNavigate();
  const { user, updateProfile, logout } = useAuth();
  const { getUserOrders, cancelOrder, refreshKey, orders: globalOrders, resendDeliveryOTP } = useOrders();
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrderForReceipt, setSelectedOrderForReceipt] = useState(null);
  const [cancelOTP, setCancelOTP] = useState('');
  const [errors, setErrors] = useState({});
  const [myOrders, setMyOrders] = useState([]);
  const [resendingOTP, setResendingOTP] = useState(false);
  const [resendOrderId, setResendOrderId] = useState(null);
  const [copiedOTP, setCopiedOTP] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    contactNumber: '',
    permanentAddress: '',
    correspondenceAddress: '',
    pinCode: '',
    landmark: '',
    village: '',
    city: 'Solapur'
  });
  const isMounted = useRef(true);
  const isLoadingProfile = useRef(false);
  const initialLoadDone = useRef(false);

  const solapurPinCodes = ['413001', '413002', '413003', '413004', '413005', '413006', '413007', '413008', '413009', '413010', '413255', '413224', '413222'];

  // Handle browser back button - go back one screen instead of closing
  useEffect(() => {
    const handlePopState = () => {
      if (onClose) {
        onClose();
      } else {
        navigate(-1);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigate, onClose]);

  // Handle resize for responsive design
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle back button click
  const handleBack = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const loadUserProfile = useCallback(async () => {
    if (!user || isLoadingProfile.current || !isMounted.current) return;
    isLoadingProfile.current = true;
    try {
      const safeUserId = getSafeUserId(user);
      const userData = await dbGet(`users/${safeUserId}`);
      if (isMounted.current) {
        if (userData && Object.keys(userData).length > 0) {
          setFormData({
            fullName: userData.fullName || userData.name || user?.name || '',
            email: userData.email || user?.email || '',
            contactNumber: userData.contactNumber || userData.phone || user?.phone || '',
            permanentAddress: userData.permanentAddress || '',
            correspondenceAddress: userData.correspondenceAddress || '',
            pinCode: userData.pinCode || '',
            landmark: userData.landmark || '',
            village: userData.village || '',
            city: userData.city || 'Solapur'
          });
        } else if (user) {
          setFormData({
            fullName: user?.name || user?.fullName || '',
            email: user?.email || '',
            contactNumber: user?.phone || user?.contactNumber || '',
            permanentAddress: user?.permanentAddress || '',
            correspondenceAddress: user?.correspondenceAddress || '',
            pinCode: user?.pinCode || '',
            landmark: user?.landmark || '',
            village: user?.village || '',
            city: user?.city || 'Solapur'
          });
        }
        setLoading(false);
      }
    } catch (error) { 
      console.error('Error loading profile:', error); 
      if (isMounted.current) setLoading(false); 
    }
    finally { isLoadingProfile.current = false; }
  }, [user]);

  const loadOrders = useCallback(() => {
    if (getUserOrders && isMounted.current) {
      const orders = getUserOrders();
      setMyOrders(Array.isArray(orders) ? orders : []);
    }
  }, [getUserOrders]);

  useEffect(() => {
    isMounted.current = true;
    if (!initialLoadDone.current && user) {
      initialLoadDone.current = true;
      loadUserProfile();
      loadOrders();
    }
    return () => { isMounted.current = false; };
  }, [user, loadUserProfile, loadOrders]);

  useEffect(() => {
    if (initialLoadDone.current && isMounted.current) {
      loadOrders();
    }
  }, [refreshKey, globalOrders, loadOrders]);

  const handleResendDeliveryOTP = async (order) => {
    if (resendingOTP) return;
    setResendingOTP(true);
    setResendOrderId(order.id);
    try {
      const identifier = order.firebaseKey || order.id;
      const result = await resendDeliveryOTP(identifier);
      if (result.success) {
        loadOrders();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setResendingOTP(false);
      setResendOrderId(null);
    }
  };

  const copyToClipboard = (text, orderId) => {
    navigator.clipboard.writeText(text);
    setCopiedOTP(orderId);
    toast.success('OTP copied to clipboard!');
    setTimeout(() => setCopiedOTP(null), 2000);
  };

  const validateFullName = (name) => { 
    if (!name.trim()) return 'Full name is required'; 
    if (name.trim().length < 3) return 'Name must be at least 3 characters'; 
    return ''; 
  };
  
  const validateContactNumber = (number) => { 
    if (!number) return 'Contact number is required'; 
    if (!/^\d{10}$/.test(number)) return 'Enter a valid 10-digit number'; 
    return ''; 
  };
  
  const validatePinCode = (pincode) => { 
    if (!pincode) return 'Pin code is required'; 
    if (!/^\d{6}$/.test(pincode)) return 'Enter valid 6-digit pin code'; 
    if (!solapurPinCodes.includes(pincode)) return 'Delivery only in Solapur City.'; 
    return ''; 
  };
  
  const validateAddress = (address) => { 
    if (!address.trim()) return 'Address is required'; 
    if (address.trim().length < 10) return 'Enter complete address'; 
    return ''; 
  };
  
  const validateForm = () => {
    const newErrors = {};
    const nameError = validateFullName(formData.fullName); 
    if (nameError) newErrors.fullName = nameError;
    const contactError = validateContactNumber(formData.contactNumber); 
    if (contactError) newErrors.contactNumber = contactError;
    const permanentAddressError = validateAddress(formData.permanentAddress); 
    if (permanentAddressError) newErrors.permanentAddress = permanentAddressError;
    const correspondenceAddressError = validateAddress(formData.correspondenceAddress); 
    if (correspondenceAddressError) newErrors.correspondenceAddress = correspondenceAddressError;
    const pinCodeError = validatePinCode(formData.pinCode); 
    if (pinCodeError) newErrors.pinCode = pinCodeError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => { 
    const { name, value } = e.target; 
    setFormData(prev => ({ ...prev, [name]: value })); 
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' })); 
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }
    
    setSaving(true);
    try {
      const safeUserId = getSafeUserId(user);
      
      const updatedData = {
        ...formData,
        name: formData.fullName,
        phone: formData.contactNumber,
        email: formData.email,
        updatedAt: new Date().toISOString()
      };
      
      const cleanData = cleanFirebaseData(updatedData);
      await dbSet(`users/${safeUserId}`, cleanData);
      await updateProfile(cleanData);
      
      toast.success('Profile updated successfully!');
      setEditing(false);
      setErrors({});
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to update profile: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const activeOrders = myOrders.filter(o => o && !['delivered', 'cancelled'].includes(o.orderStatus));
  const orderHistory = myOrders.filter(o => o && ['delivered', 'cancelled'].includes(o.orderStatus));

  const statusConfig = {
    pending: { icon: Clock, color: '#f59e0b', bg: '#fffbeb', label: 'Pending' },
    confirmed: { icon: CheckCircle, color: '#dc2626', bg: '#fff5f5', label: 'Confirmed' },
    processing: { icon: Package, color: '#dc2626', bg: '#fff5f5', label: 'Processing' },
    out: { icon: Truck, color: '#dc2626', bg: '#fff5f5', label: 'Out for Delivery' },
    delivered: { icon: CheckCircle, color: '#10b981', bg: '#e8f5e9', label: 'Delivered' },
    cancelled: { icon: XCircle, color: '#ef4444', bg: '#fef2f2', label: 'Cancelled' }
  };

  // Mobile-optimized Order Progress Component
  const OrderProgress = ({ currentStatus }) => {
    let steps = [];
    let currentStepIndex = 0;
    
    if (currentStatus === 'pending') {
      steps = [{ key: 'pending', label: 'Pending', icon: Clock, color: '#f59e0b' }];
      currentStepIndex = 0;
    } else if (currentStatus === 'confirmed') {
      steps = [
        { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: '#dc2626' },
        { key: 'delivered', label: 'Delivered', icon: CheckCircle, color: '#94a3b8' }
      ];
      currentStepIndex = 0;
    } else if (currentStatus === 'processing') {
      steps = [
        { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: '#dc2626' },
        { key: 'processing', label: 'Processing', icon: Package, color: '#dc2626' },
        { key: 'delivered', label: 'Delivered', icon: CheckCircle, color: '#94a3b8' }
      ];
      currentStepIndex = 1;
    } else if (currentStatus === 'out') {
      steps = [
        { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: '#dc2626' },
        { key: 'out', label: 'On the Way', icon: Truck, color: '#dc2626' },
        { key: 'delivered', label: 'Delivered', icon: CheckCircle, color: '#94a3b8' }
      ];
      currentStepIndex = 1;
    } else if (currentStatus === 'delivered') {
      steps = [
        { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, color: '#dc2626' },
        { key: 'out', label: 'Delivered', icon: Truck, color: '#10b981' },
        { key: 'delivered', label: 'Completed', icon: CheckCircle, color: '#10b981' }
      ];
      currentStepIndex = 2;
    } else if (currentStatus === 'cancelled') {
      steps = [{ key: 'cancelled', label: 'Cancelled', icon: XCircle, color: '#ef4444' }];
      currentStepIndex = 0;
    }

    if (isMobile) {
      return (
        <div style={{ margin: '16px 0', padding: '8px 0' }}>
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isCompleted = idx <= currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            
            return (
              <div key={step.key} style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                gap: '12px',
                marginBottom: idx < steps.length - 1 ? '20px' : '0',
                position: 'relative'
              }}>
                {idx < steps.length - 1 && (
                  <div style={{
                    position: 'absolute',
                    left: '14px',
                    top: '28px',
                    width: '2px',
                    height: 'calc(100% - 8px)',
                    background: isCompleted ? step.color : '#e2e8f0'
                  }} />
                )}
                
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: isCompleted ? step.color : 'white',
                  border: `2px solid ${isCompleted ? step.color : '#e2e8f0'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  position: 'relative'
                }}>
                  <Icon size={12} color={isCompleted ? 'white' : '#94a3b8'} />
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: isCurrent ? 600 : 400,
                    fontSize: '0.8rem',
                    color: isCompleted ? '#0f172a' : '#94a3b8'
                  }}>
                    {step.label}
                  </div>
                  {isCurrent && currentStatus === 'processing' && (
                    <div style={{ fontSize: '0.65rem', color: '#dc2626', marginTop: '2px' }}>
                      Being prepared
                    </div>
                  )}
                  {isCurrent && currentStatus === 'out' && (
                    <div style={{ fontSize: '0.65rem', color: '#dc2626', marginTop: '2px' }}>
                      Out for delivery
                    </div>
                  )}
                </div>
                
                {isCurrent && (
                  <div style={{
                    fontSize: '0.6rem',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: `${step.color}15`,
                    color: step.color,
                    fontWeight: 500
                  }}>
                    Current
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Desktop horizontal timeline
    return (
      <div style={{ margin: '20px 0', position: 'relative' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '18px', left: 0, right: 0, height: '2px', background: '#e2e8f0', zIndex: 1 }} />
          <div style={{ 
            position: 'absolute', 
            top: '18px', 
            left: 0, 
            height: '2px', 
            background: steps[currentStepIndex]?.color || '#dc2626', 
            zIndex: 2, 
            width: `${(currentStepIndex / (steps.length - 1)) * 100}%`,
            transition: 'width 0.3s ease'
          }} />
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isCompleted = idx <= currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            return (
              <div key={step.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 3 }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: isCompleted ? step.color : 'white',
                  border: `2px solid ${isCompleted || isCurrent ? step.color : '#e2e8f0'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Icon size={16} color={isCompleted ? 'white' : '#94a3b8'} />
                </div>
                <span style={{ fontSize: '0.7rem', fontWeight: 500, marginTop: '8px', color: isCompleted ? '#0f172a' : '#94a3b8' }}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleCancelOrder = async () => {
    if (cancelOTP.length !== 6) { 
      toast.error('Enter 6-digit OTP'); 
      return; 
    }
    const result = await cancelOrder(selectedOrder.id, cancelOTP);
    if (result.success) {
      toast.success(result.message);
      setMyOrders(prevOrders => prevOrders.map(order =>
        order.id === selectedOrder.id ? { ...order, orderStatus: 'cancelled' } : order
      ));
      setShowCancelModal(false);
      setSelectedOrder(null);
      setCancelOTP('');
      loadOrders();
    } else {
      toast.error(result.message);
    }
  };

  const isCancellable = (order) => {
    if (!order || order.paymentMethod !== 'Cash on Delivery') return false;
    if (!['pending', 'confirmed'].includes(order.orderStatus)) return false;
    const orderTime = new Date(order.orderDate).getTime();
    const currentTime = new Date().getTime();
    const minutesPassed = (currentTime - orderTime) / (1000 * 60);
    return minutesPassed <= 5;
  };

  const getRemainingTime = (order) => {
    if (!isCancellable(order)) return null;
    const orderTime = new Date(order.orderDate).getTime();
    const currentTime = new Date().getTime();
    const remainingMs = (5 * 60 * 1000) - (currentTime - orderTime);
    const remainingSeconds = Math.ceil(remainingMs / 1000);
    const mins = Math.floor(remainingSeconds / 60);
    const secs = remainingSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#f8fafc', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '16px' }}>
        <Loader size={40} className="spinner" style={{ color: '#dc2626' }} />
        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Loading profile...</p>
      </div>
    );
  }

  return (
    <div style={{ 
      position: 'fixed', 
      inset: 0, 
      background: '#fff5f5', 
      zIndex: 3000, 
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch'
    }}>
      {/* Sticky Header - Cherry Theme */}
      <div style={{ 
        position: 'sticky', 
        top: 0, 
        background: 'linear-gradient(135deg, #dc2626, #b91c1c)', 
        padding: isMobile ? '12px 16px' : '16px 24px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        zIndex: 50,
        boxShadow: '0 2px 10px rgba(220,38,38,0.2)'
      }}>
        <button 
          onClick={handleBack} 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            background: 'rgba(255,255,255,0.2)', 
            border: 'none', 
            padding: isMobile ? '8px 14px' : '10px 20px', 
            borderRadius: '10px', 
            cursor: 'pointer', 
            fontWeight: 500,
            fontSize: isMobile ? '0.85rem' : '0.9rem',
            color: 'white'
          }}
        >
          <ArrowLeft size={isMobile ? 18 : 20} /> Back
        </button>
        <h2 style={{ fontSize: isMobile ? '1.1rem' : '1.3rem', fontWeight: 600, margin: 0, color: 'white' }}>My Account</h2>
        <div style={{ width: '70px' }} />
      </div>

      <div style={{ 
        maxWidth: '900px', 
        margin: '0 auto', 
        padding: isMobile ? '16px' : '24px'
      }}>
        {/* Profile Header - Cherry Theme */}
        <div style={{ 
          background: 'white', 
          borderRadius: '16px', 
          padding: isMobile ? '20px' : '24px', 
          marginBottom: '20px', 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '16px' : '20px', 
          flexWrap: 'wrap',
          boxShadow: '0 4px 15px rgba(220,38,38,0.1)',
          border: '1px solid rgba(220,38,38,0.1)'
        }}>
          <div style={{ 
            width: isMobile ? '60px' : '70px', 
            height: isMobile ? '60px' : '70px', 
            borderRadius: '50%', 
            background: 'linear-gradient(135deg, #dc2626, #b91c1c)', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: 'white', 
            fontSize: isMobile ? '1.5rem' : '1.8rem', 
            fontWeight: 700,
            boxShadow: '0 4px 12px rgba(220,38,38,0.3)'
          }}>
            {formData.fullName?.[0]?.toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: isMobile ? '1rem' : '1.2rem', fontWeight: 700, marginBottom: '4px', color: '#1a0a0a' }}>
              {formData.fullName || 'User'}
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
              <Mail size={12} /> {formData.email}
            </p>
            {formData.contactNumber && (
              <p style={{ color: '#64748b', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Phone size={12} /> {formData.contactNumber}
              </p>
            )}
          </div>
          {activeTab === 'profile' && !editing && (
            <button 
              onClick={() => setEditing(true)} 
              style={{ 
                padding: isMobile ? '6px 16px' : '8px 20px', 
                background: '#fff5f5', 
                color: '#dc2626', 
                border: '1px solid #dc2626',
                borderRadius: '8px', 
                fontWeight: 600, 
                fontSize: isMobile ? '0.75rem' : '0.85rem',
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'center'
              }}
            >
              <Edit3 size={14} /> Edit Profile
            </button>
          )}
        </div>

        {/* Tabs - Cherry Theme */}
        <div style={{ 
          display: 'flex', 
          gap: '6px', 
          marginBottom: '20px', 
          background: 'white', 
          borderRadius: '12px', 
          padding: '4px', 
          border: '1px solid rgba(220,38,38,0.2)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none'
        }}>
          {[
            { id: 'profile', icon: User, label: 'Profile' },
            { id: 'active', icon: ShoppingBag, label: 'Active', count: activeOrders.length },
            { id: 'history', icon: History, label: 'History', count: orderHistory.length }
          ].map(tab => (
            <button 
              key={tab.id} 
              onClick={() => setActiveTab(tab.id)} 
              style={{ 
                flex: 1, 
                padding: isMobile ? '10px 8px' : '12px 16px', 
                borderRadius: '8px', 
                border: 'none', 
                background: activeTab === tab.id ? '#fff5f5' : 'transparent', 
                color: activeTab === tab.id ? '#dc2626' : '#64748b', 
                fontWeight: 600, 
                fontSize: isMobile ? '0.75rem' : '0.85rem', 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '6px',
                whiteSpace: 'nowrap'
              }}
            >
              <tab.icon size={isMobile ? 14 : 16} /> 
              {tab.label}
              {tab.count > 0 && (
                <span style={{ 
                  background: activeTab === tab.id ? '#dc2626' : '#e2e8f0', 
                  color: activeTab === tab.id ? 'white' : '#64748b', 
                  padding: '2px 6px', 
                  borderRadius: '12px', 
                  fontSize: '0.65rem',
                  marginLeft: '4px'
                }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Profile Edit Form - Cherry Theme */}
        {activeTab === 'profile' && (
          <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '20px' : '24px', boxShadow: '0 4px 15px rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.1)' }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <User size={14} /> Full Name *
              </label>
              <input 
                type="text" 
                name="fullName" 
                value={formData.fullName} 
                onChange={handleChange} 
                disabled={!editing} 
                style={inputStyle(editing, errors.fullName, isMobile)} 
              />
              {errors.fullName && <p style={errorStyle}>{errors.fullName}</p>}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <Mail size={14} /> Email
              </label>
              <input 
                type="email" 
                value={formData.email} 
                disabled 
                style={{ ...inputStyle(false, null, isMobile), background: '#fff5f5' }} 
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <Phone size={14} /> Contact Number *
              </label>
              <input 
                type="tel" 
                name="contactNumber" 
                value={formData.contactNumber} 
                onChange={handleChange} 
                disabled={!editing} 
                maxLength={10} 
                style={inputStyle(editing, errors.contactNumber, isMobile)} 
              />
              {errors.contactNumber && <p style={errorStyle}>{errors.contactNumber}</p>}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <Navigation size={14} /> Village / Locality
              </label>
              <input 
                type="text" 
                name="village" 
                value={formData.village} 
                onChange={handleChange} 
                disabled={!editing} 
                style={inputStyle(editing, errors.village, isMobile)} 
              />
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <Home size={14} /> Permanent Address *
              </label>
              <textarea 
                name="permanentAddress" 
                value={formData.permanentAddress} 
                onChange={handleChange} 
                disabled={!editing} 
                rows={isMobile ? 2 : 3} 
                style={{ ...inputStyle(editing, errors.permanentAddress, isMobile), resize: 'vertical' }} 
              />
              {errors.permanentAddress && <p style={errorStyle}>{errors.permanentAddress}</p>}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <Building size={14} /> Correspondence Address *
              </label>
              <textarea 
                name="correspondenceAddress" 
                value={formData.correspondenceAddress} 
                onChange={handleChange} 
                disabled={!editing} 
                rows={isMobile ? 2 : 3} 
                style={{ ...inputStyle(editing, errors.correspondenceAddress, isMobile), resize: 'vertical' }} 
              />
              {errors.correspondenceAddress && <p style={errorStyle}>{errors.correspondenceAddress}</p>}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <MapPin size={14} /> Pin Code *
              </label>
              <input 
                type="text" 
                name="pinCode" 
                value={formData.pinCode} 
                onChange={handleChange} 
                disabled={!editing} 
                maxLength={6} 
                style={inputStyle(editing, errors.pinCode, isMobile)} 
              />
              {errors.pinCode && <p style={{ ...errorStyle, color: '#f59e0b' }}>{errors.pinCode}</p>}
              {!errors.pinCode && formData.pinCode && /^\d{6}$/.test(formData.pinCode) && solapurPinCodes.includes(formData.pinCode) && (
                <p style={{ color: '#10b981', fontSize: '0.7rem', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Check size={12} /> Solapur City - Delivery available
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <MapPin size={14} /> Nearest Landmark
              </label>
              <input 
                type="text" 
                name="landmark" 
                value={formData.landmark} 
                onChange={handleChange} 
                disabled={!editing} 
                style={inputStyle(editing, errors.landmark, isMobile)} 
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ ...labelStyle, fontSize: isMobile ? '0.8rem' : '0.85rem', color: '#dc2626' }}>
                <Building size={14} /> City
              </label>
              <input 
                type="text" 
                value="Solapur, Maharashtra" 
                disabled 
                style={{ ...inputStyle(false, null, isMobile), background: '#fff5f5', fontWeight: 500 }} 
              />
              <p style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '4px' }}>
                Delivery service currently available only in Solapur City
              </p>
            </div>

            {editing && (
              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button 
                  onClick={() => { setEditing(false); setErrors({}); loadUserProfile(); }} 
                  style={{ 
                    flex: 1, 
                    padding: isMobile ? '10px' : '12px', 
                    background: '#f1f5f9', 
                    color: '#64748b', 
                    borderRadius: '10px', 
                    fontWeight: 600, 
                    fontSize: isMobile ? '0.85rem' : '0.9rem',
                    cursor: 'pointer',
                    border: 'none'
                  }}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSave} 
                  disabled={saving} 
                  style={{ 
                    flex: 1, 
                    padding: isMobile ? '10px' : '12px', 
                    background: saving ? '#94a3b8' : 'linear-gradient(135deg, #dc2626, #b91c1c)', 
                    color: 'white', 
                    borderRadius: '10px', 
                    fontWeight: 600, 
                    fontSize: isMobile ? '0.85rem' : '0.9rem',
                    cursor: saving ? 'not-allowed' : 'pointer', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    gap: '6px',
                    border: 'none'
                  }}
                >
                  {saving ? <><Loader size={16} className="spinner" /> Saving...</> : <><Save size={16} /> Save Changes</>}
                </button>
              </div>
            )}

            {!editing && (
              <button 
                onClick={logout} 
                style={{ 
                  width: '100%', 
                  marginTop: '20px', 
                  padding: isMobile ? '12px' : '14px', 
                  background: '#fef2f2', 
                  color: '#dc2626', 
                  border: '1px solid #fecaca', 
                  borderRadius: '10px', 
                  fontWeight: 600, 
                  fontSize: isMobile ? '0.85rem' : '0.9rem',
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  gap: '8px'
                }}
              >
                <LogOut size={16} /> Logout from Account
              </button>
            )}
          </div>
        )}

        {/* Active Orders Tab - Cherry Theme with Delivery OTP */}
        {activeTab === 'active' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {activeOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: isMobile ? '60px 20px' : '80px', background: 'white', borderRadius: '16px', border: '1px solid rgba(220,38,38,0.1)' }}>
                <Package size={isMobile ? 40 : 48} color="#cbd5e1" />
                <p style={{ color: '#64748b', marginTop: '12px', fontSize: isMobile ? '0.85rem' : '0.9rem' }}>No active orders</p>
              </div>
            ) : (
              activeOrders.map(order => {
                const s = statusConfig[order.orderStatus] || statusConfig.pending;
                const cancellable = isCancellable(order);
                const remainingTime = getRemainingTime(order);
                const isOutForDelivery = order.orderStatus === 'out';
                const deliveryOTP = order.deliveryOTP;
                
                return (
                  <div 
                    key={order.id} 
                    style={{ 
                      background: 'white', 
                      borderRadius: '16px', 
                      padding: isMobile ? '16px' : '20px', 
                      boxShadow: '0 4px 15px rgba(220,38,38,0.08)', 
                      border: isOutForDelivery && deliveryOTP ? '2px solid #dc2626' : '1px solid rgba(220,38,38,0.1)',
                      position: 'relative'
                    }}
                  >
                    {/* Delivery OTP Badge - Cherry Theme */}
                    {isOutForDelivery && deliveryOTP && (
                      <div style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '12px',
                        background: '#dc2626',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '0.6rem',
                        fontWeight: 600
                      }}>
                        Delivery OTP Required
                      </div>
                    )}

                    {/* Order Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: isMobile ? '0.85rem' : '0.9rem', color: '#1a0a0a' }}>
                            Order #{order.id?.slice(0, 8)}
                          </strong>
                          <span style={{ 
                            padding: '4px 10px', 
                            borderRadius: '20px', 
                            fontSize: '0.65rem', 
                            fontWeight: 600, 
                            background: s.bg, 
                            color: s.color 
                          }}>
                            {s.label}
                          </span>
                        </div>
                      </div>
                      <div style={{ fontWeight: 700, fontSize: isMobile ? '1rem' : '1.1rem', color: '#dc2626' }}>
                        ₹{order.totalAmount?.toLocaleString()}
                      </div>
                    </div>
                    
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Calendar size={12} /> {new Date(order.orderDate).toLocaleDateString()}
                    </div>
                    
                    <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: '12px' }}>
                      <strong>Payment:</strong> {order.paymentMethod}
                    </div>

                    {/* Expected Delivery Time - ABOVE Delivery OTP */}
                    {order.deliveryTime && (
                      <div style={{ 
                        margin: '12px 0', 
                        padding: '10px 12px', 
                        background: '#fff5f5', 
                        borderRadius: '10px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        border: '1px solid rgba(220,38,38,0.15)' 
                      }}>
                        <Truck size={16} color="#dc2626" />
                        <div>
                          <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#dc2626' }}>Expected Delivery: </span>
                          <span style={{ fontSize: '0.7rem', color: '#1a0a0a' }}>{order.deliveryTime}</span>
                        </div>
                      </div>
                    )}

                    {/* Delivery OTP Section - Cherry Theme */}
                    {isOutForDelivery && deliveryOTP && (
                      <div style={{
                        background: 'linear-gradient(135deg, #fff5f5, #fff)',
                        border: '1px solid #dc2626',
                        borderRadius: '12px',
                        padding: '16px',
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                          <Truck size={18} color="#dc2626" />
                          <span style={{ fontWeight: 700, color: '#dc2626', fontSize: '0.85rem' }}>
                            Your Delivery OTP
                          </span>
                        </div>
                        
                        {/* Large OTP Display */}
                        <div style={{
                          background: 'white',
                          borderRadius: '12px',
                          padding: '14px',
                          marginBottom: '12px',
                          border: '1px solid rgba(220,38,38,0.2)'
                        }}>
                          <div style={{
                            fontSize: isMobile ? '1.8rem' : '2rem',
                            fontWeight: 800,
                            letterSpacing: '6px',
                            color: '#dc2626',
                            fontFamily: 'monospace',
                            textAlign: 'center',
                            wordBreak: 'break-all'
                          }}>
                            {deliveryOTP}
                          </div>
                        </div>
                        
                        {/* Copy Button */}
                        <button
                          onClick={() => copyToClipboard(deliveryOTP, order.id)}
                          style={{
                            padding: '10px 20px',
                            background: 'white',
                            border: '1px solid #dc2626',
                            borderRadius: '8px',
                            color: '#dc2626',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <Copy size={14} />
                          {copiedOTP === order.id ? 'Copied!' : 'Copy OTP'}
                        </button>
                        
                        <p style={{ fontSize: '0.6rem', color: '#dc2626', marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                          <AlertCircle size={10} />
                          Share this OTP with the delivery person
                        </p>
                      </div>
                    )}

                    {/* Resend OTP Button */}
                    {isOutForDelivery && deliveryOTP && (
                      <button
                        onClick={() => handleResendDeliveryOTP(order)}
                        disabled={resendingOTP && resendOrderId === order.id}
                        style={{
                          width: '100%',
                          marginBottom: '12px',
                          padding: '8px',
                          background: '#eff6ff',
                          color: '#2874f0',
                          border: '1px solid #bfdbfe',
                          borderRadius: '8px',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          cursor: resendingOTP && resendOrderId === order.id ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px'
                        }}
                      >
                        {resendingOTP && resendOrderId === order.id ? (
                          <><Loader size={12} className="spinner" /> Resending...</>
                        ) : (
                          <><RefreshCw size={12} /> Resend OTP</>
                        )}
                      </button>
                    )}

                    {/* Cancellation Section */}
                    {cancellable && order.cancellationOTP && (
                      <div style={{ margin: '12px 0', background: '#fff5f5', padding: '12px', borderRadius: '8px', textAlign: 'center', border: '1px dashed #dc2626' }}>
                        <p style={{ fontWeight: 600, color: '#dc2626', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', flexWrap: 'wrap' }}>
                          <Key size={14} /> Cancellation OTP: <strong style={{ fontSize: '1rem' }}>{order.cancellationOTP}</strong>
                        </p>
                        <p style={{ fontSize: '0.6rem', color: '#dc2626', marginTop: '4px' }}>⏰ Cancel within: {remainingTime}</p>
                      </div>
                    )}

                    {/* Cancel Button */}
                    {cancellable && (
                      <button 
                        onClick={() => { setSelectedOrder(order); setShowCancelModal(true); setCancelOTP(''); }} 
                        style={{ 
                          width: '100%', 
                          marginTop: '12px', 
                          padding: '10px', 
                          background: '#fef2f2', 
                          color: '#dc2626', 
                          border: '1px solid #fecaca', 
                          borderRadius: '8px', 
                          fontWeight: 600, 
                          fontSize: '0.75rem', 
                          cursor: 'pointer' 
                        }}
                      >
                        Cancel Order
                      </button>
                    )}

                    {/* Order Items with Scroll if more than 3 */}
                    <div style={{ marginBottom: '16px' }}>
                      <strong style={{ fontSize: '0.7rem', color: '#dc2626' }}>Items:</strong>
                      <div style={{ 
                        marginTop: '8px',
                        maxHeight: order.items?.length > 3 ? '180px' : 'auto',
                        overflowY: order.items?.length > 3 ? 'auto' : 'visible',
                        paddingRight: order.items?.length > 3 ? '8px' : '0'
                      }}>
                        {order.items?.map((item, idx) => (
                          <div key={idx} style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center', 
                            gap: '12px',
                            marginBottom: idx < order.items.length - 1 ? '12px' : 0
                          }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>{item.name}</div>
                              <div style={{ fontSize: '0.65rem', color: '#64748b' }}>Qty: {item.quantity}</div>
                              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#dc2626' }}>₹{(item.price * item.quantity).toLocaleString()}</div>
                            </div>
                            <img 
                              src={item.image} 
                              alt={item.name} 
                              style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '8px', border: '1px solid rgba(220,38,38,0.2)' }}
                              onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=60&q=80'}
                            />
                          </div>
                        ))}
                      </div>
                      {order.items?.length > 3 && (
                        <div style={{ 
                          fontSize: '0.6rem', 
                          color: '#64748b', 
                          textAlign: 'center', 
                          marginTop: '8px',
                          paddingTop: '4px',
                          borderTop: '1px dashed #e2e8f0'
                        }}>
                          Scroll to see all {order.items.length} items ↓
                        </div>
                      )}
                    </div>

                    {/* Order Progress */}
                    <OrderProgress currentStatus={order.orderStatus} />

                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Order History Tab */}
        {activeTab === 'history' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {orderHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: isMobile ? '60px 20px' : '80px', background: 'white', borderRadius: '16px', border: '1px solid rgba(220,38,38,0.1)' }}>
                <History size={isMobile ? 40 : 48} color="#cbd5e1" />
                <p style={{ color: '#64748b', marginTop: '12px' }}>No order history</p>
              </div>
            ) : (
              orderHistory.map(order => {
                const s = statusConfig[order.orderStatus] || statusConfig.delivered;
                return (
                  <div 
                    key={order.id} 
                    style={{ 
                      background: 'white', 
                      borderRadius: '12px', 
                      padding: '16px', 
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)', 
                      border: '1px solid rgba(220,38,38,0.1)', 
                      opacity: order.orderStatus === 'cancelled' ? 0.8 : 1 
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <strong style={{ fontSize: '0.85rem', color: '#1a0a0a' }}>#{order.id?.slice(0, 8)}</strong>
                        <span style={{ 
                          padding: '2px 8px', 
                          borderRadius: '20px', 
                          fontSize: '0.6rem', 
                          fontWeight: 600, 
                          background: s.bg, 
                          color: s.color 
                        }}>
                          {s.label}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 700, fontSize: '0.85rem', color: '#dc2626' }}>₹{order.totalAmount?.toLocaleString()}</span>
                        <button 
                          onClick={() => setSelectedOrderForReceipt(order)} 
                          style={{ 
                            background: 'none', 
                            color: '#dc2626', 
                            border: 'none', 
                            fontSize: '0.7rem', 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <ChevronRight size={14} /> Receipt
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: '8px' }}>
                      {new Date(order.orderDate).toLocaleDateString()}
                    </div>
                    {order.deliveryTime && (
                      <div style={{ fontSize: '0.6rem', color: '#10b981', marginTop: '4px' }}>
                        Delivered on: {order.deliveryTime}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Cancel Modal - Cherry Theme */}
      {showCancelModal && selectedOrder && (
        <>
          <div 
            style={{ 
              position: 'fixed', 
              inset: 0, 
              background: 'rgba(0,0,0,0.5)', 
              zIndex: 4000,
              backdropFilter: 'blur(2px)'
            }} 
            onClick={() => setShowCancelModal(false)} 
          />
          <div style={{ 
            position: 'fixed', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)', 
            background: 'white', 
            borderRadius: '20px', 
            padding: isMobile ? '20px' : '24px', 
            zIndex: 5000, 
            width: '90%', 
            maxWidth: '400px',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            borderTop: '4px solid #dc2626'
          }}>
            <h3 style={{ fontSize: isMobile ? '1.1rem' : '1.2rem', fontWeight: 700, marginBottom: '12px', color: '#1a0a0a' }}>Cancel Order</h3>
            <p style={{ fontSize: '0.85rem', marginBottom: '4px' }}>Order #{selectedOrder.id?.slice(0, 8)}</p>
            <p style={{ fontSize: '0.7rem', color: '#dc2626', marginBottom: '16px' }}>⏰ You have {getRemainingTime(selectedOrder)} left to cancel</p>
            <p style={{ fontSize: '0.8rem', marginBottom: '12px' }}>Enter cancellation OTP shown above</p>
            
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '20px' }}>
              {[0,1,2,3,4,5].map(i => (
                <input 
                  key={i} 
                  type="text" 
                  maxLength={1} 
                  value={cancelOTP[i] || ''} 
                  onChange={e => { 
                    const newOTP = cancelOTP.split('');
                    newOTP[i] = e.target.value.replace(/\D/g, ''); 
                    setCancelOTP(newOTP.join('')); 
                    if (e.target.value && i < 5) { 
                      const next = e.target.parentElement.children[i+1]; 
                      if (next && next.focus) next.focus(); 
                    } 
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Backspace' && !cancelOTP[i] && i > 0) {
                      const prev = e.target.parentElement.children[i-1];
                      if (prev && prev.focus) prev.focus();
                    }
                  }}
                  style={{ 
                    width: isMobile ? '40px' : '45px', 
                    height: isMobile ? '45px' : '50px', 
                    textAlign: 'center', 
                    fontSize: isMobile ? '1rem' : '1.1rem', 
                    fontWeight: 700, 
                    border: cancelOTP[i] ? '2px solid #dc2626' : '2px solid #e2e8f0', 
                    borderRadius: '10px',
                    outline: 'none'
                  }}
                  autoFocus={i === 0}
                />
              ))}
            </div>
            
            <div style={{ display: 'flex', gap: '12px' }}>
              <button 
                onClick={() => { setShowCancelModal(false); setSelectedOrder(null); setCancelOTP(''); }} 
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: '#f1f5f9', 
                  borderRadius: '10px', 
                  cursor: 'pointer',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }}
              >
                Close
              </button>
              <button 
                onClick={handleCancelOrder} 
                disabled={cancelOTP.length !== 6} 
                style={{ 
                  flex: 1, 
                  padding: '12px', 
                  background: cancelOTP.length === 6 ? '#dc2626' : '#94a3b8', 
                  color: 'white', 
                  borderRadius: '10px', 
                  cursor: cancelOTP.length === 6 ? 'pointer' : 'not-allowed',
                  border: 'none',
                  fontWeight: 600,
                  fontSize: '0.85rem'
                }}
              >
                Confirm Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* Bill Receipt Modal */}
      {selectedOrderForReceipt && (
        <BillReceipt order={selectedOrderForReceipt} onClose={() => setSelectedOrderForReceipt(null)} />
      )}
      
      <style>{`
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        /* Hide scrollbar for tabs */
        ::-webkit-scrollbar {
          display: none;
        }
        
        /* Smooth scrolling */
        * {
          -webkit-tap-highlight-color: transparent;
        }
        
        /* Better touch targets */
        button {
          touch-action: manipulation;
        }
        
        /* Prevent zoom on input focus for iOS */
        @media (max-width: 768px) {
          input, textarea, select {
            font-size: 16px !important;
          }
        }
      `}</style>
    </div>
  );
};

const labelStyle = { 
  display: 'flex', 
  alignItems: 'center', 
  gap: '6px', 
  fontWeight: 600, 
  color: '#334155', 
  marginBottom: '8px' 
};

const inputStyle = (editing, hasError, isMobile) => ({
  width: '100%', 
  padding: isMobile ? '10px 12px' : '12px 14px', 
  border: hasError ? '2px solid #fca5a5' : '2px solid #e2e8f0', 
  borderRadius: '10px', 
  fontSize: isMobile ? '0.85rem' : '0.9rem', 
  background: editing ? 'white' : '#fff5f5', 
  color: editing ? '#0f172a' : '#64748b', 
  outline: 'none', 
  cursor: editing ? 'text' : 'default',
  transition: 'all 0.2s ease',
  fontFamily: 'inherit'
});

const errorStyle = { 
  color: '#dc2626', 
  fontSize: '0.7rem', 
  marginTop: '4px', 
  display: 'flex', 
  alignItems: 'center', 
  gap: '4px' 
};

export default ProfilePage;